import { useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/db8g0846oi3g3tv8umu0';

type Status = 'idle' | 'submitting' | 'success' | 'error';

interface ApiResp {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const hp = String(fd.get('website_alt') ?? '').trim();
    if (hp) {
      setStatus('success');
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === 'website_alt') return;
      const str = String(value);
      if (str.trim() !== '') payload.append(key, str);
    });

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: ApiResp | null = null;
      try {
        parsed = JSON.parse(responseText) as ApiResp;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '';
      const isSpam = /spam/i.test(String(serverMsg));

      if (res.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后重试。');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-50 px-6 py-12 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-700">
          <i className="ri-checkbox-circle-line text-3xl"></i>
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">反馈已提交，感谢你！</h3>
        <p className="mt-2 max-w-sm text-sm text-foreground-600">
          我们已收到你的留言，会尽快查看并通过邮件回复你。
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-5 inline-flex items-center gap-2 rounded-md border border-background-300/70 bg-background-100 px-5 py-2.5 font-label text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
        >
          <i className="ri-edit-line"></i> 再写一条
        </button>
      </div>
    );
  }

  return (
    <form
      id="feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-7"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-2 block font-label text-sm text-foreground-700">
            你的称呼 <span className="text-accent-600">*</span>
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的名字或昵称"
            className="w-full rounded-md border border-background-300/70 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none transition-colors focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-2 block font-label text-sm text-foreground-700">
            邮箱 <span className="text-accent-600">*</span>
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="用于接收我们的回复"
            className="w-full rounded-md border border-background-300/70 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none transition-colors focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="fb-topic" className="mb-2 block font-label text-sm text-foreground-700">
          反馈类型
        </label>
        <select
          id="fb-topic"
          name="topic"
          defaultValue="内容建议"
          className="w-full rounded-md border border-background-300/70 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
        >
          <option value="内容建议">内容建议</option>
          <option value="资料纠错">资料纠错</option>
          <option value="合作咨询">合作咨询</option>
          <option value="其他反馈">其他反馈</option>
        </select>
      </div>

      <div className="mt-5">
        <label htmlFor="fb-message" className="mb-2 block font-label text-sm text-foreground-700">
          留言内容 <span className="text-accent-600">*</span>
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你的问题或建议，最多 500 字"
          className="w-full resize-none rounded-md border border-background-300/70 bg-background-100 px-4 py-3 text-sm leading-7 text-foreground-900 placeholder:text-foreground-500 outline-none transition-colors focus:border-primary-400"
        ></textarea>
      </div>

      {/* 反垃圾隐藏字段 */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-hp-guard"
      />

      {status === 'error' && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
          <i className="ri-error-warning-line"></i>
          {formError || '提交失败，请稍后重试。'}
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
      >
        <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
        {status === 'submitting' ? '提交中…' : '提交反馈'}
      </button>
    </form>
  );
}